import React from "react";
import {
  FaReact,
  FaNodeJs,
  FaDatabase,
  FaCode,
  FaShieldAlt,
  FaChartLine,
  FaBlog,
  FaUsers,
  FaCogs,
  FaMobile,
} from "react-icons/fa";
import {
  SiTailwindcss,
  SiTypescript,
  SiMongodb,
  SiJavascript,
} from "react-icons/si";

const Aboutweb = () => {
  const technologies = [
    {
      name: "React.js",
      icon: FaReact,
      color: "text-blue-500",
      description:
        "Modern UI framework for building interactive user interfaces",
    },
    {
      name: "Node.js",
      icon: FaNodeJs,
      color: "text-green-600",
      description:
        "Server-side JavaScript runtime for scalable backend development",
    },
    {
      name: "TypeScript",
      icon: SiTypescript,
      color: "text-blue-600",
      description:
        "Type-safe JavaScript for better code quality and maintainability",
    },
    {
      name: "MongoDB",
      icon: SiMongodb,
      color: "text-green-500",
      description: "NoSQL database for flexible data storage and retrieval",
    },
    {
      name: "Tailwind CSS",
      icon: SiTailwindcss,
      color: "text-cyan-500",
      description: "Utility-first CSS framework for rapid UI development",
    },
    {
      name: "JavaScript",
      icon: SiJavascript,
      color: "text-yellow-500",
      description: "Core programming language for web development",
    },
  ];

  const features = [
    {
      icon: FaShieldAlt,
      title: "Secure Admin Dashboard",
      description:
        "Comprehensive admin panel with role-based access control and secure authentication system.",
      color: "bg-blue-50 text-blue-600",
    },
    {
      icon: FaChartLine,
      title: "Analytics & Insights",
      description:
        "Real-time visitor analytics with IP tracking, geographic data, and user behavior insights.",
      color: "bg-green-50 text-green-600",
    },
    {
      icon: FaBlog,
      title: "Content Management",
      description:
        "Full CRUD operations for blog posts, skills, projects, and portfolio content management.",
      color: "bg-purple-50 text-purple-600",
    },
    {
      icon: FaUsers,
      title: "User Engagement",
      description:
        "Interactive contact forms, message management, and visitor tracking capabilities.",
      color: "bg-orange-50 text-orange-600",
    },
    {
      icon: FaCogs,
      title: "Advanced Features",
      description:
        "Image upload handling, rich text editing, and automated data processing workflows.",
      color: "bg-indigo-50 text-indigo-600",
    },
    {
      icon: FaMobile,
      title: "Responsive Design",
      description:
        "Mobile-first approach ensuring optimal experience across all devices and screen sizes.",
      color: "bg-pink-50 text-pink-600",
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            About This Website
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            A modern, full-stack portfolio website showcasing professional
            development capabilities with cutting-edge technologies and
            enterprise-grade features.
          </p>
        </div>

        <div className="mb-20">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Technology Stack
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {technologies.map((tech, index) => (
              <div
                key={index}
                className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow duration-300"
              >
                <div className="flex items-center mb-4">
                  <tech.icon className={`text-3xl ${tech.color} mr-4`} />
                  <h3 className="text-xl font-semibold text-gray-900">
                    {tech.name}
                  </h3>
                </div>
                <p className="text-gray-600 leading-relaxed">
                  {tech.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        <div className="mb-20">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Key Features & Capabilities
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              >
                <div
                  className={`inline-flex p-3 rounded-lg ${feature.color} mb-4`}
                >
                  <feature.icon className="text-2xl" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {feature.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-8">
            Technical Highlights
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <FaCode className="text-blue-600 mr-3" />
                Frontend Architecture
              </h3>
              <ul className="space-y-3 text-gray-600">
                <li className="flex items-start">
                  <span className="text-blue-500 mr-2">•</span>
                  Component-based architecture with reusable UI components
                </li>
                <li className="flex items-start">
                  <span className="text-blue-500 mr-2">•</span>
                  Responsive design with mobile-first approach
                </li>
                <li className="flex items-start">
                  <span className="text-blue-500 mr-2">•</span>
                  Modern state management with React Hooks
                </li>
                <li className="flex items-start">
                  <span className="text-blue-500 mr-2">•</span>
                  Optimized performance with lazy loading
                </li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <FaDatabase className="text-green-600 mr-3" />
                Backend Infrastructure
              </h3>
              <ul className="space-y-3 text-gray-600">
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">•</span>
                  RESTful API with Express.js framework
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">•</span>
                  JWT-based authentication and authorization
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">•</span>
                  MongoDB with Mongoose ODM for data modeling
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">•</span>
                  File upload handling with Multer middleware
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="text-center mt-16">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-8 md:p-12 text-white">
            <h3 className="text-2xl md:text-3xl font-bold mb-4">
              Ready to Build Something Amazing?
            </h3>
            <p className="text-blue-100 text-lg mb-6 max-w-2xl mx-auto">
              This portfolio demonstrates the power of modern web technologies.
              Let's collaborate to bring your ideas to life with the same level
              of quality and innovation.
            </p>
            <button
              onClick={() =>
                document
                  .getElementById("contact")
                  ?.scrollIntoView({ behavior: "smooth" })
              }
              className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors duration-200"
            >
              Get In Touch
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Aboutweb;
